import { computed, type Ref } from "vue";

import type {
  Document,
  GraphDatabase,
  GraphRecordResult,
  GraphResult,
  GraphResultTableObjectGraphErrorResult,
  OpenaiMessage,
} from "@ctrl/controller-rpc";

import { EMPTY_DOCUMENT, isDocument } from "@/components/document";
import { markdownToDocument } from "@/components/editor/markdown";
import type {
  GraphRelationResult,
  GraphRelationResultChoice,
  GraphRelationRoutine,
} from "@/components/graph/graph-relation";
import { fromGraphDocument } from "@/components/graph/properties/document-serialization";
import type { GraphDateData } from "@/components/graph/properties/types";
import { extractTitleFromPlainRecord } from "@/components/graph/title";
import type { BaseLogger } from "@/components/inject";
import { parseMonth } from "@/utils/dates";

/**
 * Type guard to check if a GraphResult is a boolean.
 */
export function asBoolean(result: GraphResult): boolean | undefined {
  return typeof result === "boolean" ? result : undefined;
}

/**
 * Type guard to check if a GraphResult is a number.
 */
export function asNumber(result: GraphResult): number | undefined {
  return typeof result === "number" ? result : undefined;
}

/**
 * Type guard to check if a GraphResult is a bigint.
 */
export function asBigInt(result: GraphResult): bigint | undefined {
  return typeof result === "bigint" ? result : undefined;
}

/**
 * Convert a GraphResult count to a JS number.
 *
 * The graph can return counts as `number` or `bigint` (e.g. `421n`).
 * If the bigint exceeds Number.MAX_SAFE_INTEGER, returns MAX_SAFE_INTEGER.
 */
export function asCountNumber(result: GraphResult): number | undefined {
  const n = asNumber(result);
  if (n !== undefined) return n;
  const b = asBigInt(result);
  if (b === undefined) return undefined;
  const max = BigInt(Number.MAX_SAFE_INTEGER);
  if (b > max) return Number.MAX_SAFE_INTEGER;
  if (b < 0n) return 0;
  return Number(b);
}

/**
 * Coerce a graph integer (`number` or IPC `bigint`) to a JS number.
 * Unlike {@link asCountNumber}, this does not clamp negatives or overflow.
 */
export function asGraphInt(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "bigint") return Number(value);
  return undefined;
}

/**
 * Type guard to check if a GraphResult is a string.
 */
export function asString(result: GraphResult): string | undefined {
  return typeof result === "string" ? result : undefined;
}

/**
 * Type guard to check if a GraphResult is an Array<GraphResult>.
 */
export function asArray(result: GraphResult): Array<GraphResult> | undefined {
  return Array.isArray(result) ? result : undefined;
}

/**
 * Type guard to check if a GraphResult is a Map<GraphResult, GraphResult>.
 */
export function asMap(
  result: GraphResult
): Map<GraphResult, GraphResult> | undefined {
  return result instanceof Map ? result : undefined;
}

/**
 * Type guard for "plain objects" (not arrays, not Maps).
 * Useful for safely introspecting `unknown` / `GraphResult` record-ish values.
 */
export function isPlainRecord(
  value: unknown
): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Parse a graph date record. Controller values may use capitalized keys
 * (`Year`/`Month`/`Day`) with no `kind` tag, or the frontend shape
 * (`year`/`month`/`day`, `kind: "date"`). Year/Day are often `bigint` over IPC.
 */
export function asGraphDateRecord(value: unknown): GraphDateData | undefined {
  if (!isPlainRecord(value)) return undefined;
  const year = asGraphInt(value.year ?? value.Year);
  const day = asGraphInt(value.day ?? value.Day);
  const month = parseMonth(value.month ?? value.Month);
  if (year === undefined || day === undefined || !month) return undefined;
  return { year, month, day, kind: "date" };
}

/**
 * Type guard to check if a GraphResult is a GraphRecordResult.
 * Returns the record if it's a plain object (not array, Map, or primitive),
 * otherwise returns undefined.
 */
export function asGraphRecordResult(
  result: GraphResult
): GraphRecordResult | undefined {
  return isGraphRecordResult(result) ? result : undefined;
}

/**
 * A field's cell on a graph row, looked up by column id first, then by the
 * field's display name (row keys are usually column ids, but some query
 * results and drafts key by name). `undefined` when the record is not a
 * record or the cell is unset.
 */
export function recordCell(
  record: GraphResult | null | undefined,
  fieldId: string,
  fieldName?: string
): GraphResult | undefined {
  const graphRecord = record != null ? asGraphRecordResult(record) : undefined;
  if (!graphRecord) return undefined;
  const byId = graphRecord[fieldId];
  if (byId !== undefined && byId !== null) return byId;
  if (!fieldName) return undefined;
  const byName = graphRecord[fieldName];
  return byName === undefined || byName === null ? undefined : byName;
}

export type EmbeddedGraphDatabase = {
  id: GraphDatabase["id"];
  name?: GraphDatabase["name"];
  workspace?: GraphDatabase["workspace"];
  record: GraphRecordResult;
};

/** Validate graph database metadata without claiming a full database. */
export function asEmbeddedGraphDatabase(
  value: GraphResult | undefined
): EmbeddedGraphDatabase | undefined {
  if (value === undefined) return undefined;
  const database = asGraphRecordResult(value);
  if (!database) return undefined;

  const id = asString(database.id);
  if (!id) return undefined;
  const name = asString(database.name);
  const workspace = asString(database.workspace);
  return {
    id,
    ...(name ? { name } : {}),
    ...(workspace ? { workspace } : {}),
    record: database,
  };
}

/** Read a graph-embedded `_routine.database` without claiming a full database. */
export function extractEmbeddedDatabase(
  value: GraphResult | undefined
): EmbeddedGraphDatabase | undefined {
  if (value === undefined) return undefined;
  const record = asGraphRecordResult(value);
  const routine = record && asGraphRecordResult(record._routine);
  return routine && asEmbeddedGraphDatabase(routine.database);
}

/** Build a graph row reference, omitting absent database metadata. */
export function routineRowRef(
  id: string,
  database?: EmbeddedGraphDatabase
): GraphRecordResult {
  return {
    _routine: database
      ? { id, database: database.record }
      : {
          id,
        },
  };
}

export function parseGraphRelationResult(
  value: GraphResult
): GraphRelationResult | undefined {
  const record = asGraphRecordResult(value);
  const routine = record && asGraphRecordResult(record._routine);
  if (!record || !routine) return undefined;
  const id = asString(routine.id);
  if (!id) return undefined;

  const parsedRoutine: GraphRelationRoutine = { ...routine, id };
  return { ...record, _routine: parsedRoutine };
}

export function parseGraphRelationResults(
  value: GraphResult
): GraphRelationResult[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const relations = value.flatMap((entry) => {
    const relation = parseGraphRelationResult(entry);
    return relation ? [relation] : [];
  });
  return relations.length > 0 || value.length === 0 ? relations : undefined;
}

/**
 * A relation cell as a list of relation records: a multi-relation cell is a
 * list, but a single-relation cell is the bare record, so wrap it. Anything
 * else (unset, malformed) yields an empty list.
 */
export function graphRelationRecords(
  value: GraphResult
): GraphRelationResult[] {
  const many = parseGraphRelationResults(value);
  if (many) return many;
  const single = parseGraphRelationResult(value);
  return single ? [single] : [];
}

export function graphRelationChoice(
  value: GraphResult
): GraphRelationResultChoice | undefined {
  const relation = parseGraphRelationResult(value);
  return relation ? { ...relation, id: relation._routine.id } : undefined;
}

/**
 * Unwrap the result of `controller.graph.object.get(...)`.
 */
export function unwrapGraphObjectGetResult(
  raw: GraphResultTableObjectGraphErrorResult
): GraphResult | null {
  if ("error" in raw) return null;
  return raw.value.data;
}

/**
 * Runtime guard for recursively validating a GraphResult from `unknown`.
 */
export function isGraphResult(value: unknown): value is GraphResult {
  return isGraphResultValue(value, new WeakSet());
}

export function isGraphRecordResult(
  value: GraphResult
): value is GraphRecordResult {
  return isPlainRecord(value);
}

function isGraphResultValue(value: unknown, seen: WeakSet<object>): boolean {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "bigint" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (typeof value !== "object") return false;
  if (Array.isArray(value)) {
    if (seen.has(value)) return true;
    seen.add(value);
    return value.every((entry) => isGraphResultValue(entry, seen));
  }
  if (value instanceof Map) {
    if (seen.has(value)) return true;
    seen.add(value);
    return Array.from(value).every(
      ([key, entry]) =>
        isGraphResultValue(key, seen) && isGraphResultValue(entry, seen)
    );
  }
  if (!isPlainRecord(value)) return false;
  if (seen.has(value)) return true;
  seen.add(value);
  return Object.values(value).every((entry) => isGraphResultValue(entry, seen));
}

/**
 * Read the `_routine.type` string when present.
 *
 * Many controller-provided record values include a type hint in the shape:
 * `{ _routine: { type: string } }`.
 */
export function routineTypeFromGraphResult(
  value: GraphResult
): string | undefined {
  const record = asGraphRecordResult(value);
  if (!record) return undefined;
  const routine = asGraphRecordResult(record["_routine"]);
  if (!routine) return undefined;
  return asString(routine["type"]);
}

export function isRoutineType(value: GraphResult, expected: string): boolean {
  return routineTypeFromGraphResult(value) === expected;
}

/**
 * Traverse an arbitrary GraphResult and extract "object"-typed blocks.
 *
 * We look for records with:
 * - `type === "object"`
 * - `workspace_id`, `table_id`, `row_id` (strings)
 */
export type ObjectBlock = {
  block_id: string;
  workspace_id: string;
  table_id: string;
  row_id: string;
};

export function extractObjectBlockRefs(value: GraphResult): ObjectBlock[] {
  const out: ObjectBlock[] = [];
  const visit = (v: GraphResult) => {
    const arr = asArray(v);
    if (arr) {
      for (const item of arr) visit(item);
      return;
    }
    const map = asMap(v);
    if (map) {
      for (const [k, val] of map.entries()) {
        visit(k);
        visit(val);
      }
      return;
    }
    const rec = asGraphRecordResult(v);
    if (rec) {
      const type = asString(rec["type"]);
      const block_id = asString(rec["id"]);
      const workspace_id = asString(rec["workspace_id"]);
      const table_id = asString(rec["table_id"]);
      const row_id = asString(rec["row_id"]);
      if (type === "object" && block_id && workspace_id && table_id && row_id) {
        out.push({ block_id, workspace_id, table_id, row_id });
      }
      for (const val of Object.values(rec)) {
        visit(val);
      }
    }
  };
  visit(value);
  return out;
}

/**
 * Extract the row id from a graph "routine reference" record.
 *
 * Many graph relation-like values use the shape:
 * `{ _routine: { id: string, database: ... } }`
 *
 * This helper returns that `id` when present.
 */
export function extractRoutineRowId(value: GraphResult): string | undefined {
  const record = asGraphRecordResult(value);
  if (!record) return undefined;
  const routine = asGraphRecordResult(record["_routine"]);
  if (!routine) return undefined;
  return asString(routine["id"]);
}

/**
 * Extract the table id from a graph "routine reference" record.
 *
 * Many graph relation-like values use the shape:
 * `{ _routine: { id: string, database: { id: string, ... } } }`
 *
 * This helper returns that `database.id` when present.
 */
export function extractRoutineDatabaseId(
  value: GraphResult
): string | undefined {
  const record = asGraphRecordResult(value);
  if (!record) return undefined;
  const routine = asGraphRecordResult(record["_routine"]);
  if (!routine) return undefined;
  const database = asGraphRecordResult(routine["database"]);
  if (!database) return undefined;
  return asString(database["id"]);
}

/**
 * Convert a GraphResult into a stable-ish identifier string.
 *
 * This is useful whenever you need a consistent string representation for
 * "group keys" (e.g. for persistence keys, caches, etc.).
 *
 * Notes:
 * - For relation-like records, we prefer `_routine.id` when present.
 * - For plain records without a stable id, we fall back to common name/title
 *   fields, then a generic "object".
 * - If you need the output to be safe as a URL/path/storage-key segment,
 *   apply `encodeURIComponent(...)` at the call site.
 */
export function graphResultToIdentifier(value: GraphResult): string {
  if (asNull(value) === null) return "null";

  const s = asString(value);
  if (s !== undefined) return s;

  const n = asNumber(value);
  if (n !== undefined) return String(n);

  const b = asBoolean(value);
  if (b !== undefined) return b ? "true" : "false";

  if (asArray(value)) return "array";
  if (asMap(value)) return "map";

  const routineId = extractRoutineRowId(value);
  if (routineId) return routineId;

  const record = asGraphRecordResult(value);
  if (record) {
    const id = asString(record["id"]);
    if (id) return id;

    return extractTitleFromPlainRecord(record) ?? "object";
  }

  return String(value);
}

/**
 * Extract routine row ids from a list of relation-like GraphResult values.
 *
 * Expects a list in the shape: `[ { _routine: { id, database } }, ... ]`.
 */
export function extractRoutineRowIdsFromList(value: GraphResult): string[] {
  const list = asArray(value);
  if (!list) return [];
  const out: string[] = [];
  for (const item of list) {
    const id = extractRoutineRowId(item);
    if (id) out.push(id);
  }
  return out;
}

/**
 * Type guard to check if a GraphResult is null.
 */
export function asNull(result: GraphResult): null | undefined {
  return result === null ? result : undefined;
}

export function getJsonProperty(
  objectData: Ref<GraphResult | undefined>,
  propertyName: string
): Ref<GraphResult | undefined> {
  return computed(() => {
    const result = objectData.value;
    const record = asGraphRecordResult(result ?? null);
    if (!record) return undefined;
    return record[propertyName];
  });
}

const VALID_ROLES = ["user", "assistant", "system", "developer"] as const;

const openaiRole = (value: string): OpenaiMessage["role"] | undefined =>
  VALID_ROLES.find((role) => role === value);

export function parseHistoryJson(
  value: GraphResult | undefined
): Array<OpenaiMessage> {
  const list = asArray(value ?? null);
  if (!list) return [];
  const out: Array<OpenaiMessage> = [];
  for (const entry of list) {
    const r = asGraphRecordResult(entry);
    if (!r) continue;
    const role = asString(r["role"]);
    const content = asString(r["content"]);
    if (!role || !content) continue;
    const validRole = openaiRole(role);
    if (!validRole) continue;
    out.push({
      role: validRole,
      content,
    });
  }
  return out;
}

export function notesFromGraphResult(
  value: GraphResult | undefined,
  logger: BaseLogger
): Document {
  if (value == null) return EMPTY_DOCUMENT;
  const s = asString(value);
  if (s) return markdownToDocument(s, logger);
  if (isDocument(value)) return value;
  try {
    return fromGraphDocument(value);
  } catch {
    return EMPTY_DOCUMENT;
  }
}

export function getNotesProperty(
  objectData: Ref<GraphResult | undefined>,
  propertyName: string,
  logger: BaseLogger
): Ref<Document> {
  return computed(() => {
    const record = asGraphRecordResult(objectData.value ?? null);
    if (!record) return EMPTY_DOCUMENT;
    return notesFromGraphResult(record[propertyName], logger);
  });
}

/**
 * Extract the value from a GraphResult known to be of type "json".
 * Handles both wrapper form { type: "Json"|"json", value: any } and raw value.
 */
export function asJson(result: GraphResult | null | undefined): unknown {
  if (result === null || result === undefined) return undefined;
  if (
    isPlainRecord(result) &&
    (result.type === "json" || result.type === "Json") &&
    "value" in result
  ) {
    return result.value;
  }
  return result;
}
